// Core imports
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// Misc imports
import * as d3 from 'd3';

const TopicModellerClusterChart = ({ topicsData, showOccurences = false, isActive = false }) => {
  const d3Container = useRef(null);

  useEffect(() => {
    if (d3Container.current && topicsData?.length > 0 && isActive) {
      // Clear the container
      d3.select(d3Container.current).selectAll('*').remove();

      const transformData = (data) => {
        return {
          name: 'root',
          children: data.map(topic => ({
            ...( showOccurences && { value: topic?.corpus?.reduce((acc, item) => acc + item?.occurrences, 0) }),
            name: topic?.name,
            children: topic?.topics?.length > 0
              ? topic?.topics?.map(subtopic => ({
                name: subtopic?.name,
                children: subtopic?.corpus?.map(item => ({ name: item.label, value: item?.occurrences > 0 ? item.occurrences : 1 })),
                ...( showOccurences && { value: subtopic?.corpus?.reduce((acc, item) => acc + item?.occurrences, 0) })
              }))
              : topic?.corpus?.map(item => ({ name: item.label, value: item?.occurrences > 0 ? item.occurrences : 1 }))
          }))
        };
      };

      const data = transformData(topicsData);

      // Use the dimensions of the parent container
      const width = d3Container.current.clientWidth;
      const height = width;  // Maintain a square aspect ratio

      const color = d3.scaleLinear()
        .domain([0, 5])
        .range(['hsla(200, 100%, 97%, 1)', 'hsla(227, 19%, 48%, 1)'])
        .interpolate(d3.interpolateHcl);

      const pack = data => d3.pack()
        .size([width, height])
        .padding(3)(d3.hierarchy(data)
          .sum(d => d.value)
          .sort((a, b) => b.value - a.value));

      const root = pack(data);

      const svg = d3.select(d3Container.current)
        .append('svg')
        .attr('viewBox', `-${width / 2} -${height / 2} ${width} ${height}`)
        .attr('width', width)
        .attr('height', height)
        .attr('style', `max-width: 100%; height: auto; display: block; margin: 0 -14px; background: rgba(255, 255, 255, 0); cursor: pointer;`);

      const node = svg.append('g')
        .selectAll('circle')
        .data(root.descendants().slice(1))
        .join('circle')
        .attr('fill', d => d.children ? color(d.depth) : '#ebedf4')
        .attr('pointer-events', d => !d.children ? 'none' : null)
        .on('mouseover', function () {
          d3.select(this).attr('stroke', '#AFC1D2');
        })
        .on('mouseout', function () {
          d3.select(this).attr('stroke', null);
        })
        .on('click', (event, d) => focus !== d && (zoom(event, d), event.stopPropagation()));

      const labelGroup = svg.append('g')
        .style('font', '12px Noto Sans')
        .style('font-weight', '700')
        .attr('text-anchor', 'middle');

      const wrapText = (text, radius) => {
        text.each(function () {
          const text = d3.select(this),
            words = text.text().split(/\s+/).reverse(),
            lineHeight = 1.1, // ems
            y = text.attr('y'),
            dy = parseFloat(text.attr('dy')),
            x = 0;

          let word,
            line = [],
            lineNumber = 0,
            tspan = text.text(null).append('tspan').attr('x', x).attr('y', y).attr('dy', `${dy}em`);

          while (words.length > 0) {
            word = words.pop();
            line.push(word);
            tspan.text(line.join(' '));
            if (tspan.node().getComputedTextLength() > 2 * radius) {
              line.pop();
              tspan.text(line.join(' '));
              line = [word];
              tspan = text.append('tspan').attr('x', x).attr('y', y).attr('dy', `${++lineNumber * lineHeight + dy}em`).text(word);
            }
          }

          // Center the text within the circle
          const tspans = text.selectAll('tspan');
          const totalHeight = tspans.nodes().length * lineHeight;
          tspans.attr('y', (_, i) => -totalHeight / 2 + i * lineHeight + 0.35);

          // Store the number of lines for each text element
          text.node().lineCount = tspans.nodes().length;
        });
      };

      // Main label text
      const label = labelGroup.selectAll('text')
        .data(root.descendants())
        .join('text')
        .attr('dy', '0.35em')
        .style('fill-opacity', d => d.parent === root ? 1 : 0)
        .style('display', d => d.parent === root ? 'inline' : 'none')
        .text(d => d.data.name.length > 20 && d.parent !== root ? `${d.data.name.slice(0, 13)}... ${d.data.value ? `(${d.data.value} occ.)` : ''}` : `${d.data.name} ${d.data.value ? `(${d.data.value} occ.)` : ''}`)
        .attr('transform', d => `translate(${d.x},${d.y})`) // Position it correctly
        .each(function(d) {
          wrapText(d3.select(this), d.r);
        });

      label.append('title')
        .text(d => d.data.name);

      const zoomTo = (v) => {
        const k = width / v[2];
        view = v;
        label.attr('transform', d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
        node.attr('transform', d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`);
        node.attr('r', d => d.r * k);
      };

      svg.on('click', (event) => zoom(event, root));

      let focus = root;
      let view;
      zoomTo([focus.x, focus.y, focus.r * 2]);

      const zoom = (event, d) => {
        focus = d;
        const transition = svg.transition()
          .duration(event.altKey ? 7500 : 750)
          .tween('zoom', d => {
            const i = d3.interpolateZoom(view, [focus.x, focus.y, focus.r * 2]);
            return t => zoomTo(i(t));
          });

        label
          .filter(function (d) {
            return d.parent === focus || this.style.display === 'inline';
          })
          .transition(transition)
          .style('fill-opacity', d => d.parent === focus ? 1 : 0)
          .on('start', function (d) {
            if (d.parent === focus) {
              this.style.display = 'inline';
            }
          })
          .on('end', function (d) {
            if (d.parent !== focus) {
              this.style.display = 'none';
            }
          });
      };
    }

    return () => {
      if (!isActive || topicsData.length === 0) {
        d3.select(d3Container.current).selectAll('*').remove();
      }
    };
  }, [topicsData, isActive]);

  return (
    <div ref={d3Container} className="w-50 h-50"></div>
  );
};

TopicModellerClusterChart.propTypes = {
  topicsData: PropTypes.array,
  isActive: PropTypes.bool,
  showOccurences: PropTypes.bool
};

export default TopicModellerClusterChart;
